/**
 * Bottom-of-sidebar (and bottom-of-drawer) profile chip.
 * Shows the family name + plan + a small menu with Settings + Sign out.
 *
 * Uses the existing /v1/families/me query (there's no user-me endpoint),
 * so the displayed name is the family's, not the parent's personal name.
 */
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Settings, LogOut, ChevronDown, CreditCard } from 'lucide-react';
import { usersApi } from '@/api/users';
import { logout } from '@/api/auth';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

export function ProfileChip() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const family = useQuery({
    queryKey: ['family-me'],
    queryFn: usersApi.myFamily,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const initial = (family.data?.name?.trim()[0] ?? 'K').toUpperCase();
  const name = family.data?.name ?? 'Your family';
  const plan = family.data?.plan;

  async function onLogout() {
    try {
      await logout();
    } catch {
      /* still proceed to login */
    }
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2.5 rounded-md p-2 text-left transition-colors hover:bg-[color:var(--kz-color-surface-sunken)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--kz-color-brand-primary)]/30"
        >
          <Avatar className="size-9 shrink-0">
            <AvatarFallback className="bg-gradient-to-br from-[#4f6bff] to-[#ff7a59] font-semibold text-white">
              {initial}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-[color:var(--kz-color-ink-primary)]">{name}</p>
            {plan && (
              <p className="truncate text-xs capitalize text-[color:var(--kz-color-ink-muted)]">{plan} plan</p>
            )}
          </div>
          <ChevronDown className="size-4 shrink-0 text-[color:var(--kz-color-ink-muted)]" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side="top"
          align="start"
          sideOffset={6}
          className="z-50 min-w-44 rounded-md border border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-card)] p-1 shadow-xl"
        >
          <DropdownMenu.Item
            asChild
            className="cursor-pointer rounded-sm px-2 py-2 text-sm outline-none data-[highlighted]:bg-[color:var(--kz-color-surface-sunken)]"
          >
            <Link to="/settings" className="flex items-center gap-2">
              <Settings className="size-4" />
              Settings
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            asChild
            className="cursor-pointer rounded-sm px-2 py-2 text-sm outline-none data-[highlighted]:bg-[color:var(--kz-color-surface-sunken)]"
          >
            <Link to="/billing" className="flex items-center gap-2">
              <CreditCard className="size-4" />
              Billing
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-[color:var(--kz-color-border-subtle)]" />
          <DropdownMenu.Item
            onSelect={onLogout}
            className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-2 text-sm text-[color:var(--kz-color-status-danger,#dc2626)] outline-none data-[highlighted]:bg-[color:var(--kz-color-surface-sunken)]"
          >
            <LogOut className="size-4" />
            Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
